import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Hash } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { Input } from "./ui/input";

function SeriesRow({ s, canEdit }) {
  const qc = useQueryClient();
  const [f, setF] = useState({ prefix: s.prefix, digits: s.digits, next_number: s.next_number });
  useEffect(() => { setF({ prefix: s.prefix, digits: s.digits, next_number: s.next_number }); }, [s.prefix, s.digits, s.next_number]);
  const digits = Math.min(10, Math.max(1, Number(f.digits) || 1));
  const example = `${f.prefix}${String(Math.max(1, Number(f.next_number) || 1)).padStart(digits, "0")}`;
  const dirty = f.prefix !== s.prefix || Number(f.digits) !== s.digits || Number(f.next_number) !== s.next_number;
  const save = useMutation({
    mutationFn: async () => (await api.put(`/number-series/${s.id}`, { prefix: f.prefix.trim(), digits: Number(f.digits), next_number: Number(f.next_number) })).data,
    onSuccess: (r) => { qc.invalidateQueries({ queryKey: ["number-series"] }); toast.success(`${s.label}: next number ${r.example}`); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });
  const cell = "py-1.5 px-2";
  return (
    <tr className="border-b border-border/60" data-testid={`series-row-${s.id}`}>
      <td className={`${cell} font-medium whitespace-nowrap`}>{s.label}</td>
      <td className={cell}><Input value={f.prefix} disabled={!canEdit} maxLength={15} onChange={(e) => setF({ ...f, prefix: e.target.value.replace(/\s/g, "") })} className="h-8 w-24 rounded-sm font-mono text-xs" data-testid={`series-prefix-${s.id}`} /></td>
      <td className={cell}><Input type="number" min={1} max={10} value={f.digits} disabled={!canEdit} onChange={(e) => setF({ ...f, digits: e.target.value })} className="h-8 w-16 rounded-sm text-xs" data-testid={`series-digits-${s.id}`} /></td>
      <td className={cell}><Input type="number" min={1} value={f.next_number} disabled={!canEdit} onChange={(e) => setF({ ...f, next_number: e.target.value })} className="h-8 w-24 rounded-sm text-xs" data-testid={`series-next-${s.id}`} /></td>
      <td className={`${cell} font-mono text-xs whitespace-nowrap`} data-testid={`series-example-${s.id}`}>{example}</td>
      <td className={`${cell} text-right`}>{canEdit && <Button size="sm" disabled={!dirty || save.isPending} onClick={() => save.mutate()} className="h-8 rounded-sm" data-testid={`series-save-${s.id}`}>Save</Button>}</td>
    </tr>
  );
}

export const NumberSeries = () => {
  const { user } = useAuth();
  const canEdit = user?.role === "admin";
  const { data = [] } = useQuery({ queryKey: ["number-series"], queryFn: async () => (await api.get("/number-series")).data });
  return (
    <div className="lg:col-span-3 border border-border bg-card p-5" data-testid="number-series-card">
      <p className="font-bold text-sm mb-1 flex items-center gap-2"><Hash size={16} className="text-primary" /> Document Numbers</p>
      <p className="text-xs text-muted-foreground mb-4">Set the prefix, number of digits and next number for each document type. You can also type your own number on any new quote, order, invoice or credit memo. Numbers that are already used are always skipped or blocked.</p>
      <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[560px]">
          <thead><tr className="text-left border-b border-border text-[11px] uppercase tracking-wide text-muted-foreground">
            <th className="py-2 px-2">Document</th><th className="py-2 px-2">Prefix</th><th className="py-2 px-2">Digits</th><th className="py-2 px-2">Next number</th><th className="py-2 px-2">Next will be</th><th />
          </tr></thead>
          <tbody>{data.map((s) => <SeriesRow key={s.id} s={s} canEdit={canEdit} />)}</tbody>
        </table>
      </div>
      {!canEdit && <p className="text-[11px] text-muted-foreground mt-2">Only administrators can change number series.</p>}
    </div>
  );
};
